import type { Breadcrumb, Event } from '@sentry/react'

// Keeps personal and secret data out of what Sentry receives. Every error event
// and breadcrumb passes through here before it leaves the browser: e-mail
// addresses, bank account numbers and credentials are replaced, request
// cookies and bodies are dropped, and the user is reduced to an id. On-chain
// addresses and transaction hashes are public and stay as they are.

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g
const AUTH_SCHEME = /\b(bearer|basic)\s+[A-Za-z0-9._+/=-]{8,}/gi
const KEY_VALUE = /\b(api[-_]?key|secret|password|passwd|pwd|authorization|private[-_]?key|mnemonic|otp|2fa[-_]?code)\b\s*[:=]\s*(?:(?:bearer|basic)\s+)?[^\s,;"']+/gi
const IBAN = /\b[A-Z]{2}\d{2}(?:\s?[A-Z0-9]){11,30}\b/g
const SENSITIVE_KEY = /password|passwd|secret|authorization|cookie|api[-_]?key|private[-_]?key|mnemonic|bearer|credential|session[-_]?token|otp/i

export const scrubText = (text: string): string =>
  text
    .replace(JWT, '[token]')
    .replace(KEY_VALUE, '$1: [redacted]')
    .replace(AUTH_SCHEME, '$1 [redacted]')
    .replace(EMAIL, '[email]')
    .replace(IBAN, '[account]')

export const scrubValue = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return scrubText(value)
  if (value === null || typeof value !== 'object' || depth > 8) return value
  if (Array.isArray(value)) return value.map(item => scrubValue(item, depth + 1))
  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SENSITIVE_KEY.test(key) ? '[redacted]' : scrubValue(item, depth + 1)
  }
  return out
}

export const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb => ({
  ...crumb,
  message: crumb.message === undefined ? undefined : scrubText(crumb.message),
  data: crumb.data === undefined ? undefined : (scrubValue(crumb.data) as Breadcrumb['data'])
})

export const scrubEvent = <T extends Event>(event: T): T => {
  if (event.message) event.message = scrubText(event.message)
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.value) exception.value = scrubText(exception.value)
    }
  }
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  if (event.extra) event.extra = scrubValue(event.extra) as Event['extra']
  if (event.tags) event.tags = scrubValue(event.tags) as Event['tags']
  if (event.contexts) event.contexts = scrubValue(event.contexts) as Event['contexts']
  if (event.request) {
    const { url, method, headers } = event.request
    event.request = {
      url: url === undefined ? undefined : scrubText(url),
      method,
      headers: headers === undefined ? undefined : (scrubValue(headers) as Record<string, string>)
    }
  }
  if (event.user) event.user = { id: event.user.id }
  return event
}
